<!-- Header like the provided design -->
<div
  class="bg-purple-100 rounded-3xl px-4 py-5 flex items-center justify-between shadow-sm"
>
  <div
    class="text-2xl md:text-3xl font-extrabold text-black flex justify-between px-4"
  >
    Account settings
  </div>
  <button
    (click)="save()"
    [disabled]="isSaving"
    [attr.aria-busy]="isSaving"
    class="bg-[#2E294E] text-white rounded-full px-6 py-3 text-xs tracking-[0.2em] shadow-[0_4px_0_0_rgba(46,41,78,0.2)] active:translate-y-[2px] active:shadow-[0_6px_0_0_rgba(46,41,78,0.2)] disabled:opacity-60 disabled:cursor-not-allowed relative inline-flex items-center justify-center"
  >
    <!-- Invisible sizing content to reserve width -->
    <span class="invisible inline-flex items-center gap-2 whitespace-nowrap">
      <svg class="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true"></svg>
      <span>SAVE CHANGES</span>
    </span>
    <!-- Actual dynamic content layered on top -->
    <span class="absolute inset-0 flex items-center justify-center gap-2">
      <svg
        *ngIf="isSaving"
        class="w-4 h-4 animate-spin"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
      <span class="whitespace-nowrap">{{
        isSaving ? "SAVING…" : "SAVE CHANGES"
      }}</span>
    </span>
  </button>
</div>

<div class="mt-4 bg-white/90 rounded-2xl shadow-sm overflow-hidden">
  <div class="px-4 py-3 bg-white">
    <div class="text-center text-xs tracking-[0.2em] text-black font-semibold">
      PERSONAL DETAILS
    </div>
  </div>

  <div class="p-4 md:px-8 space-y-4">
    <!-- Date of birth -->
    <div>
      <div class="text-xs font-semibold mb-1">Date of birth</div>
      <div class="flex items-center gap-2">
        <input
          type="date"
          class="flex-1 w-full border rounded-lg px-3 py-2 text-sm"
          placeholder="DD/MM/YYYY"
          [(ngModel)]="profile.dateOfBirth"
        />
      </div>
    </div>

    <!-- Height -->
    <div>
      <div class="text-xs font-semibold mb-1">
        Height
        <span class="text-gray-500" *ngIf="userUnits.lengthMetric; else impLbl"
          >(cm)</span
        >
        <ng-template #impLbl
          ><span class="text-gray-500">(ft / in)</span></ng-template
        >
      </div>

      <!-- Metric: single input in cm -->
      <div
        *ngIf="userUnits.lengthMetric; else imperialHeight"
        class="flex items-center gap-2"
      >
        <input
          type="number"
          step="1"
          class="flex-1 w-full border rounded-lg px-3 py-2 text-sm"
          placeholder="165.0"
          [(ngModel)]="profile.heightInCm"
          (ngModelChange)="recalculate()"
        />
      </div>

      <!-- Imperial: seamless two-input control for ft/in -->
      <ng-template #imperialHeight>
        <div
          class="flex w-full items-center border h-[38px] border-black rounded-lg overflow-hidden bg-white gap-1 px-2 py-1"
        >
          <input
            type="number"
            min="0"
            inputmode="numeric"
            pattern="[0-9]*"
            class="flex-none w-16 px-3 py-2 text-sm border-none text-left"
            placeholder="5"
            aria-label="Height feet"
            [(ngModel)]="heightValueImperialInputFt"
            (ngModelChange)="onImperialHeightChange()"
          />'
          <input
            type="number"
            min="0"
            inputmode="numeric"
            class="flex-none w-16 px-3 py-2 text-sm border-none text-left"
            placeholder="7"
            aria-label="Height inches"
            [(ngModel)]="heightValueImperialInputIn"
            (ngModelChange)="onImperialHeightChange()"
          />"
        </div>
      </ng-template>
    </div>

    <!-- Weight -->
    <div>
      <div class="text-xs font-semibold mb-1">
        Weight in {{ userUnits.massMetric ? "kg" : "lbs" }}
      </div>
      <div class="flex items-center gap-2">
        <input
          type="number"
          inputmode="numeric"
          step="1"
          class="flex-1 w-full border rounded-lg px-3 py-2 text-sm"
          [placeholder]="userUnits.massMetric ? '56 kg' : '124 lbs'"
          [(ngModel)]="weightInput"
          (ngModelChange)="onWeightInputChange()"
        />
      </div>
    </div>
  </div>
</div>

<div class="mt-4 p-4 md:px-8 bg-white/90 rounded-2xl shadow-sm overflow-hidden">
  <div class="px-4 py-3 bg-white">
    <div class="text-center text-xs tracking-[0.2em] text-black font-semibold">
      UNITS
    </div>
  </div>
  <div class="p-2 divide-y">
    <div class="flex items-center justify-between py-3">
      <div class="text-xs font-semibold">Weight</div>
      <div
        class="inline-flex rounded-md border border-gray-300 overflow-hidden shadow-sm min-w-44"
      >
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [ngClass]="
            userUnits?.massMetric
              ? 'bg-black text-white'
              : 'bg-white text-gray-900'
          "
          (click)="setUnit('massMetric', true)"
        >
          kg
        </button>
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [ngClass]="
            userUnits && !userUnits.massMetric
              ? 'bg-black text-white'
              : 'bg-white text-gray-900'
          "
          (click)="setUnit('massMetric', false)"
        >
          lbs
        </button>
      </div>
    </div>
    <div class="flex items-center justify-between py-3">
      <div class="text-xs font-semibold">Length</div>
      <div
        class="inline-flex rounded-md border border-gray-300 overflow-hidden shadow-sm min-w-44"
      >
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [class.bg-black]="userUnits?.lengthMetric"
          [class.text-white]="userUnits?.lengthMetric"
          (click)="setUnit('lengthMetric', true)"
        >
          metric
        </button>
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [class.bg-black]="userUnits && !userUnits.lengthMetric"
          [class.text-white]="userUnits && !userUnits.lengthMetric"
          (click)="setUnit('lengthMetric', false)"
        >
          imperial
        </button>
      </div>
    </div>
    <div class="flex items-center justify-between py-3">
      <div class="text-xs font-semibold">Temperature</div>
      <div
        class="inline-flex rounded-md border border-gray-300 overflow-hidden shadow-sm min-w-44"
      >
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [class.bg-black]="userUnits?.temperatureCelsius"
          [class.text-white]="userUnits?.temperatureCelsius"
          (click)="setUnit('temperatureCelsius', true)"
        >
          C
        </button>
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [class.bg-black]="userUnits && !userUnits.temperatureCelsius"
          [class.text-white]="userUnits && !userUnits.temperatureCelsius"
          (click)="setUnit('temperatureCelsius', false)"
        >
          F
        </button>
      </div>
    </div>
    <div class="flex items-center justify-between py-3">
      <div class="text-xs font-semibold">Water intake</div>
      <div
        class="inline-flex rounded-md border border-gray-300 overflow-hidden shadow-sm min-w-44"
      >
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [class.bg-black]="userUnits?.volumeMetric"
          [class.text-white]="userUnits?.volumeMetric"
          (click)="setUnit('volumeMetric', true)"
        >
          liter
        </button>
        <button
          class="flex-1 text-center px-5 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1"
          [class.bg-black]="userUnits && !userUnits.volumeMetric"
          [class.text-white]="userUnits && !userUnits.volumeMetric"
          (click)="setUnit('volumeMetric', false)"
        >
          oz
        </button>
      </div>
    </div>
  </div>
</div>
